/*
 * AT1-597 #76 — "There is a border around Bag menu after pressing" (Safari,
 * macOS + iOS).
 *
 * The border is the project focus ring, not a border. frontend/focus-visible.css
 * has `*:focus-visible { outline: 2px solid currentColor; outline-offset: -2px }`
 * — an INSET ring, so on the 32x14 Bag link it reads as a box drawn around the
 * word rather than a ring outside it.
 *
 * What puts focus back on the link is close(): `removeTrapFocus(this.activeElement)`
 * calls .focus() on the trigger so keyboard users return to where they started.
 * Verified in-browser: immediately after closing the drawer, activeElement is
 * A#cart-icon-link with computed `outline: rgb(255,255,255) solid 2px`,
 * `outline-offset: -2px`.
 *
 * Keyboard users must keep that ring (WCAG 2.4.7 / AT1-517), so it is dropped
 * only when the interaction came from a pointer. Modality is tracked here rather
 * than read off an event because the drawer's close button is wired inline as
 * `onclick="this.closest('cart-drawer').close()"` and passes no event at all.
 *
 * Verified: pointer open + pointer close -> activeElement BODY (no ring);
 * keyboard open + Escape -> activeElement A#cart-icon-link (ring kept).
 */
let cartDrawerPointerInteraction = false;
document.addEventListener(
  'pointerdown',
  () => {
    cartDrawerPointerInteraction = true;
  },
  { capture: true, passive: true }
);
document.addEventListener(
  'keydown',
  () => {
    cartDrawerPointerInteraction = false;
  },
  { capture: true, passive: true }
);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-link');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      // AT1-597 #76 — see the note at the top of this file. Opening by pointer
      // must not leave the ring on the trigger; opening by keyboard must.
      if (cartDrawerPointerInteraction) cartLink.blur();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // AT1-597 #76 — record the modality of THIS open on the host so CSS can drop
    // the dialog's focus ring for pointer opens only; keyboard opens keep it.
    // Safari treats the programmatic focus that trapFocus() puts on
    // `.drawer__inner` (tabindex="-1") as :focus-visible, Chrome does not, which
    // is why the ring appeared on a plain click in Safari. See
    // frontend/focus-visible.css. Written on EVERY open, so the class can never
    // leak in from a previous interaction of the other kind.
    this.classList.toggle('drawer--pointer-open', cartDrawerPointerInteraction);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    // AT1-597 #76 — see the note at the top of this file. removeTrapFocus()
    // just re-focused the Bag link; for a pointer-driven close that focus is
    // what paints the ring, so drop it. Keyboard closes (Escape, Enter/Space on
    // the X) keep the focus and the ring.
    if (cartDrawerPointerInteraction) this.activeElement?.blur();
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  handleGWPandUpsell() {
    setTimeout(() => {
      this.autoAddGWP();
      this.autoRemoveGWP();
      this.removeGWP();
      this.addGwp();
      this.renderCartUpsell();
      this.updateThresholdMessage();
      this.updateShippingMessage();
    }, 1000);
  }

  autoAddGWP() {
    const ELM_cartItems = this.querySelector('.cart-items');
    if (!ELM_cartItems) return;
    const isAutoAdd = ELM_cartItems.dataset.autoAddGwp;
    const ELM_gwp = document.querySelector('.cart-gwp');
    const ELM_gwpProduct = ELM_gwp.querySelector('.cart-gwp__product');
    const ELM_gwpProductSubmit = ELM_gwpProduct?.querySelector('.quick-add__submit');

    if (isAutoAdd === 'true' && ELM_gwpProductSubmit) {
      ELM_gwpProductSubmit?.click();
    }
  }

  addGwp() {
    const ELM_thresholds = document.querySelector('.thresholds-message');
    if (!ELM_thresholds) return;
    const ELM_addGwpText = ELM_thresholds.querySelector('.thresholds-message__text-changed__add-gwp');
    const ELM_gwpProductHide = document.querySelector('.cart-gwp__product--hidden');
    const ELM_gwpProductHideSubmit = ELM_gwpProductHide?.querySelector('.quick-add__submit');

    ELM_addGwpText?.addEventListener('click', () => {
      ELM_gwpProductHideSubmit?.click();
    });
  }

  autoRemoveGWP() {
    const ELM_cartItems = this.querySelector('.cart-items');
    if (!ELM_cartItems) return;
    const isAutoRemove = ELM_cartItems.dataset.autoRemoveGwp;
    const ELM_gwpItem = ELM_cartItems.querySelector('.cart-item--gwp');

    if (ELM_gwpItem && isAutoRemove === 'true') {
      const ELM_remove = ELM_gwpItem?.querySelector('cart-remove-button');
      ELM_remove && ELM_remove.click();
    }
  }

  removeGWP() {
    setTimeout(() => {
      const ELM_cartItems = this.querySelector('.cart-items');
      if (!ELM_cartItems) return;
      const ELM_gwpItem = ELM_cartItems.querySelector('.cart-item--gwp');
      const ELM_gwp = document.querySelector('.cart-gwp');
      const ELM_gwpProduct = ELM_gwp.querySelector('.cart-gwp__product');
      const ELM_gwpProductWrapper = ELM_gwpProduct.querySelector('.card-wrapper');
      const cartDrawer = document.querySelector('.cart-drawer');

      if (ELM_gwpItem) {
        const ELM_remove = ELM_gwpItem.querySelector('cart-remove-button');
        ELM_remove.addEventListener('click', (e) => {
          e.preventDefault();
          ELM_gwpProductWrapper?.remove();
          cartDrawer.classList.add('removed-gwp');
        });
      }
    }, 280);
  }

  renderCartUpsell() {
    const ELM_cartUpsell = document.querySelector('.cart-upsell');
    const ELM_cartItemUpsell = document.getElementById('CartDrawer-Item-Upsell');

    if (ELM_cartItemUpsell && ELM_cartUpsell) {
      ELM_cartItemUpsell.innerHTML = ELM_cartUpsell?.innerHTML;

      const items = ELM_cartItemUpsell.querySelectorAll('.swiper-slide');
      items?.forEach(item => {
        const buttonSubmit = item.querySelector('.quick-add__submit');
        const variants = item.querySelectorAll('[data-custom-select-list] li');
        if (variants.length > 1) {
          buttonSubmit?.classList.add('disabled');
        }

        variants?.forEach(variant => {
          variant?.addEventListener('click', (e) => {
            const target = e.currentTarget;
            const id_value = target.getAttribute('data-value');
            ELM_cartUpsell.querySelector(`[data-custom-select-list] li[data-value="${id_value}"]`).click();

            if (id_value.length > 0) {
              buttonSubmit?.classList.remove('disabled');
            } else {
              buttonSubmit?.classList.add('disabled');
            }
          });
        });

        buttonSubmit?.addEventListener('click', (e) => {
          e.preventDefault();
          const product_id = buttonSubmit.getAttribute('data-product-id');
          ELM_cartUpsell.querySelector(`.swiper-wrapper button.quick-add__submit[data-product-id="${product_id}"]`).click();
          document.getElementById('CartDrawer').classList.add('cart-drawer--ctl-product-added');
          localStorage.setItem('isCartUpSell', 'show');
        });
      });

      const summary = document.querySelector('#CartDrawer-Item-Upsell .cart-upsell__header');
      summary?.addEventListener('click', () => {
        summary.closest('.cart-upsell__details').toggleAttribute('open');
      });
    }
  }

  updateThresholdMessage() {
    const thresHoldMessage = document.querySelector('.thresholds-message__text.hidden');
    setTimeout(() => {
      thresHoldMessage?.classList.remove('hidden');
    });
  }

  updateShippingMessage() {
    const globalShippingMessage = this.getAttribute('data-global-shipping-message');
    const taxShippingMessage = this.querySelector('.cart-drawer__footer-taxes--global-shipping');
    if (taxShippingMessage) {
      taxShippingMessage.innerHTML = `${globalShippingMessage}`;
    }
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.handleGWPandUpsell();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'shopify-section-cart-drawer',
        section: 'cart-drawer',
        selector: '.cart-upsell',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
